/**
 * PE-2087 — product ↔ taxonomy links for the product create/update payload.
 *
 * ⚠️ TWO SEPARATE ID NAMESPACES — do NOT merge these with `categoryId`.
 *
 *  • `categoryId` (scalar, REQUIRED) is resolved by rack-api against its OWN `Category`
 *    table (`rack-api/src/domain/product/services/product.service.ts` → `prisma.category
 *    .findFirst`). It is the legacy single hierarchical category.
 *  • `collectionIds` / `categoryIds` / `occasionIds` (arrays, OPTIONAL) are PRODUCT-SERVICE
 *    ids — the many-to-many browsing taxonomy, applied via `ProductServiceCatalogueWriter
 *    .setTaxonomy`. These are the ids `GET /categories|/collections|/occasions` return.
 *
 * The two tables have no id overlap; an unfinished catalogue cut-over is why both exist.
 * Feeding one namespace into the other is a separately filed backend bug, not a cleanup —
 * leave the scalar alone.
 *
 * Each array key is OMITTED when empty: rack-api only touches a set when the caller
 * actually supplied it ("omit to leave membership untouched"), so sending `[]` would
 * CLEAR the product's existing links instead of leaving them as they are.
 */
export interface ProductTaxonomySelection {
  collectionIds?: string[]
  categoryIds?: string[]
  occasionIds?: string[]
}

const nonEmptyIds = (ids: string[] | undefined): string[] =>
  (ids ?? []).map(id => id?.trim()).filter((id): id is string => !!id)

/** Spreadable fragment: present keys only, never an empty array. */
export const buildTaxonomyPayload = (selection: ProductTaxonomySelection): ProductTaxonomySelection => {
  const payload: ProductTaxonomySelection = {}
  const collectionIds = nonEmptyIds(selection.collectionIds)
  const categoryIds = nonEmptyIds(selection.categoryIds)
  const occasionIds = nonEmptyIds(selection.occasionIds)

  if (collectionIds.length > 0) payload.collectionIds = collectionIds
  if (categoryIds.length > 0) payload.categoryIds = categoryIds
  if (occasionIds.length > 0) payload.occasionIds = occasionIds

  return payload
}
